import type { SalesforceLinkFormState } from '@/features/SalesforceAccounts/hooks/useSalesforceLinkForm'
import { storedAccountIdOf } from '@/features/SalesforceAccounts/lib/salesforceOutcome'
import { Box, Button, HStack, Heading, Text, VStack } from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'

type Props = {
  /**
   * The value the operator was shown when the dialog opened — the card's GET
   * result. Absent on a first link, where the operator was shown nothing.
   */
  expectedAccountId?: string
  /**
   * `previousSalesforceAccountId` from the 409 body: what the service says is
   * stored right now. **Absent means "no stored value to show you"**, not
   * "unlinked", so its presence decides whether this panel can offer a retry
   * at all.
   *
   * Typed as nullable because the wire value is: the contract refuses a token
   * supplied for an unlinked organisation *with a null* value here, while the
   * generated type claims `string | undefined`.
   */
  previousAccountId?: string | null
  /** `confirmationRequired` or `conflict`. Picks the heading and its blurb. */
  messageKey: string
  /**
   * Retry with `previousAccountId` as the compare-and-swap token. Rendered
   * only when there is a token to send.
   */
  onConfirm?: (token: string) => void
  /** Re-read the link and restart the form with the typed values intact. */
  onReRead?: () => void
  /** The form's own rule, e.g. a reason that a confirmed retry still needs. */
  isConfirmDisabled?: boolean
  /** Why confirm is unavailable. Shown next to the buttons when set. */
  confirmHint?: string
  /** The last re-read failed, so the stored value is still unknown. */
  hasReReadFailed?: boolean
  isPending: boolean
  /** Chooses the confirm label: replacing a link, or clearing one. */
  variant: 'repoint' | 'clear'
}

type ConflictValueRowProps = {
  label: string
  value: string
  isMuted?: boolean
}

function ConflictValueRow({
  label,
  value,
  isMuted = false,
}: Readonly<ConflictValueRowProps>) {
  return (
    <HStack gap={2} align="baseline" w="100%">
      <Text fontSize="xs" color="text.text-subtle" minW="8rem">
        {label}
      </Text>
      <Text
        fontFamily={isMuted ? undefined : 'mono'}
        fontSize="sm"
        color={isMuted ? 'text.text-subtle' : undefined}
        wordBreak="break-all"
      >
        {value}
      </Text>
    </HStack>
  )
}

/**
 * The `409` state, shared by the link, re-point and clear forms.
 *
 * Deliberately **not** an error `Alert`: a `confirmation_required` is the
 * service asking the operator to confirm what they are overwriting, not
 * reporting a fault, and their typed values are still live behind it.
 *
 * Two mutually exclusive branches, keyed on whether the 409 echoed a token:
 *
 *  - **present** — the confirm button re-fires the write with that exact
 *    string as the compare-and-swap token. Byte-for-byte: no trim, no case
 *    fold, no padding to 18 characters, since a normalised token fails the
 *    write with no visible cause.
 *  - **absent** — `undefined`, `null` *or* `''`, all three — there is nothing
 *    to confirm *against*, so the only way out is a re-read. No submit
 *    affordance is rendered, because any token it could send would be
 *    invented.
 */
export function LinkConflictPanel({
  expectedAccountId,
  previousAccountId,
  messageKey,
  onConfirm,
  onReRead,
  isConfirmDisabled = false,
  confirmHint,
  hasReReadFailed = false,
  isPending,
  variant,
}: Readonly<Props>) {
  const { t } = useTranslation('features', { useSuspense: false })

  // The one place the "is there a token?" question is answered. `null` is what
  // the contract sends on the path that matters, and `null !== undefined`, so
  // comparing against `undefined` alone would render a confirm button that
  // submits an invented token.
  const storedAccountId = storedAccountIdOf(previousAccountId)

  const confirmLabelKey =
    variant === 'clear'
      ? 'salesforceLink.conflict.confirmClearButton'
      : 'salesforceLink.conflict.confirmRepointButton'

  return (
    <Box
      borderWidth="1px"
      borderColor="border.emphasized"
      borderRadius="md"
      bg="bg.muted"
      p={4}
      data-testid="salesforce-link-conflict-panel"
    >
      <VStack align="stretch" gap={3}>
        <Heading as="h3" fontSize="md" m={0}>
          {String(t(`salesforceLink.conflict.heading.${messageKey}`))}
        </Heading>
        <Text fontSize="sm">
          {String(t(`salesforceLink.conflict.description.${messageKey}`))}
        </Text>

        <VStack align="stretch" gap={1}>
          {expectedAccountId ? (
            <ConflictValueRow
              label={String(t('salesforceLink.conflict.youWereShownLabel'))}
              value={expectedAccountId}
            />
          ) : null}
          <ConflictValueRow
            label={String(t('salesforceLink.conflict.storedNowLabel'))}
            value={
              storedAccountId ??
              String(t('salesforceLink.conflict.unknownValue'))
            }
            isMuted={storedAccountId === undefined}
          />
        </VStack>

        {storedAccountId === undefined ? (
          <Text fontSize="sm" color="text.text-subtle">
            {String(t('salesforceLink.conflict.unknownExplanation'))}
          </Text>
        ) : null}

        {hasReReadFailed ? (
          <Text fontSize="sm" color="fg.error" role="alert">
            {String(t('salesforceLink.conflict.reReadFailed'))}
          </Text>
        ) : null}

        {confirmHint && storedAccountId !== undefined ? (
          <Text fontSize="sm" color="text.text-subtle">
            {confirmHint}
          </Text>
        ) : null}

        <HStack gap={2}>
          {onReRead ? (
            <Button
              variant="outline"
              size="sm"
              disabled={isPending}
              onClick={onReRead}
            >
              {String(t('salesforceLink.conflict.reReadButton'))}
            </Button>
          ) : null}
          {/* `storedAccountId` is passed straight through: the
              compare-and-swap needs the service's own string, unaltered. */}
          {storedAccountId !== undefined && onConfirm ? (
            <Button
              colorPalette="brand"
              size="sm"
              loading={isPending}
              disabled={isPending || isConfirmDisabled}
              onClick={() => onConfirm(storedAccountId)}
            >
              {String(t(confirmLabelKey, { accountId: storedAccountId }))}
            </Button>
          ) : null}
        </HStack>
      </VStack>
    </Box>
  )
}

type FormConflictProps = {
  form: SalesforceLinkFormState
  /** The value the card read, i.e. what the operator was shown. */
  expectedAccountId?: string
  /** Why confirm is held shut, when this form holds it shut. */
  confirmHint?: string
  variant: 'repoint' | 'clear'
}

/**
 * The panel wired to a `useSalesforceLinkForm` state, so all three forms take
 * the same six values off that state without drifting apart.
 *
 * The caller still decides *whether* to render it: `SalesforceLinkFormDialog`
 * treats a present `conflictPanel` as "this is a conflict, not a failure", so
 * a component that renders null would suppress the error alert.
 */
export function SalesforceLinkFormConflict({
  form,
  expectedAccountId,
  confirmHint,
  variant,
}: Readonly<FormConflictProps>) {
  return (
    <LinkConflictPanel
      expectedAccountId={expectedAccountId}
      previousAccountId={form.storedAccountId}
      messageKey={form.conflictMessageKey}
      onConfirm={form.confirm}
      onReRead={form.reRead}
      isConfirmDisabled={form.confirmDisabled}
      confirmHint={confirmHint}
      hasReReadFailed={form.reReadFailed}
      isPending={form.isBusy}
      variant={variant}
    />
  )
}
